import { useSessionUser, useUserCatalog } from "hooks/useSession";
import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { UploadCloud } from "lucide-react";
import { Button } from "components/common";
import { useProjectsApi } from "hooks/useProjects";
import { useProjectsSummary } from "hooks/useProjects/useProjectsSummary";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace/useWorkspaceIdentifiers";
import { WorkspaceRole } from "hooks/useWorkspace/workspace.types";
import {
  ProjectsLoadingState,
  ProjectsEmptyState,
  ProjectsLoadErrorState,
  ProjectsHeader,
  ProjectsSearchAndFilters,
  ProjectsGrid,
  StartProduction,
  isAcceptedScreenplay,
  isFileDrag,
} from "./components";
import { ProjectsSort } from "./components/ProjectsSearchAndFilters/ProjectsSearchAndFilters.types";
import { getProjectPath, productionTypeMapping } from "./ProjectsPage.constants";
import {
  dismissClaudeConnectorBanner,
  isClaudeConnectorBannerDismissed,
} from "utils/claudeConnectorStorage";
import { AiAppsPromo, ClaudeConnectorCard } from "pages/settings/workspace/connections/components";
import { useCreateProjectLauncher } from "hooks/project";
import { useAxios } from "hooks/useAxios";
import { useAnalytics } from "hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "analytics";
import { ENVS, PATH_WORKSPACE, USER_API } from "config";
import { handleToast } from "utils/toasts";
import { PROJECT_CREATE_TIMEOUT_MS } from "services/createProjectTask";
import { useOptionalUserPreferences } from "providers/user-preferences";
import { pickLandingWorkspace } from "utils/workspace/landingWorkspace";
import {
  markDemoLandingSeen,
  pickDemoLandingProject,
  shouldWaitForSeededDemo,
} from "./utils/demoLanding";
import { useSeededDemoWait } from "./utils/useSeededDemoWait";

dayjs.extend(relativeTime);

/** Search appears once the grid is long enough to need it. */
const SEARCH_THRESHOLD = 7;

const createdAtValue = (project: { createdAt?: string }) =>
  project.createdAt ? dayjs(project.createdAt).valueOf() : 0;

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const axios = useAxios();
  const { trackEvent } = useAnalytics();
  const preferencesContext = useOptionalUserPreferences();
  const patchPreferences = preferencesContext?.patchPreferences;
  const { workspaceId } = useWorkspaceIdentifiers();
  const {
    launchCreateProject,
    createProjectFlowElement,
    isCreateProjectOpen,
    canCreateWorkspaceProject,
    isWorkspaceAuthLoading,
  } = useCreateProjectLauncher();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<ProjectsSort>("newest");
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  // Only the workspace in the URL. Counts, search, empty states, and the demo
  // button all read this list, so they stay in that workspace too.
  const {
    projects,
    isLoading,
    isError: isProjectsError,
    refetch,
  } = useProjectsApi({
    take: 100,
    order: "DESC",
    workspaceId,
  });
  const { session, isOneTimePurchase } = useSessionUser();
  const {
    data: catalog,
    refetch: refetchCatalog,
    isFetching: isCatalogFetching,
    isFetchedAfterMount: isCatalogFetchedAfterMount,
  } = useUserCatalog();
  const profile = session
    ? { ...session, workspaces: catalog?.workspaces ?? [], isOneTimePurchase }
    : null;
  const defaultWorkspace = profile?.workspaces?.[0];
  const effectiveWorkspaceId = workspaceId ?? defaultWorkspace?.workspace_id ?? "";
  const currentWorkspace = profile?.workspaces?.find(
    (workspace) => String(workspace.workspace_id) === String(effectiveWorkspaceId)
  );
  const workspaceName = currentWorkspace?.name;
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const [aiAppsPromoHidden, setAiAppsPromoHidden] = useState(false);

  const canCreateProject = !isWorkspaceAuthLoading && canCreateWorkspaceProject;
  const productionProjects = projects.filter((project) => !project.isDemo);
  const isFirstRun = productionProjects.length === 0;
  const entrySource = isFirstRun ? "onboarding" : "projects";

  const showAiAppsPromo = Boolean(
    profile?.user_id && !aiAppsPromoHidden && !isClaudeConnectorBannerDismissed(profile.user_id)
  );

  const handleDismissAiAppsPromo = () => {
    if (!profile?.user_id) return;
    dismissClaudeConnectorBanner(profile.user_id);
    setAiAppsPromoHidden(true);
  };

  const handleScreenplayFile = (file: File) => {
    if (!isAcceptedScreenplay(file)) {
      handleToast(
        "That file type isn't supported. Use a PDF, Final Draft, Fountain, or Celtx file.",
        "error"
      );
      return;
    }
    launchCreateProject({ initialFile: file, entrySource });
  };

  const handleStartBlank = () => {
    launchCreateProject({ initialSource: "blank", entrySource });
  };

  const handleExploreDemo = async () => {
    const existingDemo = projects.find((project) => project.isDemo && !project.archived);
    trackEvent(ANALYTICS_EVENTS.PROJECT_SETUP_STARTED, {
      entry_source: entrySource,
      workspace_id: effectiveWorkspaceId,
    });
    trackEvent(ANALYTICS_EVENTS.PROJECT_SETUP_SOURCE_SELECTED, {
      source: "demo",
      entry_source: entrySource,
      workspace_id: effectiveWorkspaceId,
    });
    patchPreferences?.({
      onboarding: {
        dismissed: false,
        projectSetupStatus: "source_selected",
        projectSetupSource: "demo",
        entrySource,
      },
    });

    if (existingDemo) {
      navigate(getProjectPath(effectiveWorkspaceId, existingDemo.projectId, "script/breakdown"));
      return;
    }

    setIsDemoLoading(true);
    try {
      // Name the workspace being viewed; without it the backend uses the
      // user's default workspace, which may be a different one.
      // Copying the demo can take 10 to 20 seconds, past the default request timeout.
      const response = (await axios.post(
        USER_API.addDemoProject,
        { workspace_id: effectiveWorkspaceId },
        { timeout: PROJECT_CREATE_TIMEOUT_MS }
      )) as unknown as {
        data?: { project_id?: string; workspace_id?: string };
        project_id?: string;
        workspace_id?: string;
      };
      const projectId = response.data?.project_id ?? response.project_id;
      const demoWorkspaceId = response.data?.workspace_id ?? response.workspace_id;
      await refetch();
      if (!projectId) {
        throw new Error("Demo project response did not include a project ID");
      }
      if (demoWorkspaceId && String(demoWorkspaceId) !== String(effectiveWorkspaceId)) {
        throw new Error("Demo project was added to a different workspace");
      }
      const completedAt = new Date().toISOString();
      trackEvent(ANALYTICS_EVENTS.PROJECT_CREATED, {
        project_id: projectId,
        workspace_id: effectiveWorkspaceId,
        source: "demo",
      });
      trackEvent(ANALYTICS_EVENTS.SCREENPLAY_ADDED, {
        project_id: projectId,
        workspace_id: effectiveWorkspaceId,
        source: "demo",
      });
      patchPreferences?.({
        onboarding: {
          dismissed: false,
          projectSetupStatus: "screenplay_ready",
          projectSetupSource: "demo",
          entrySource,
          projectId,
          projectCreatedAt: completedAt,
          screenplayReadyAt: completedAt,
        },
      });
      navigate(getProjectPath(effectiveWorkspaceId, projectId, "script/breakdown"));
    } catch {
      handleToast("We couldn't prepare the demo project. Please try again.", "error");
    } finally {
      setIsDemoLoading(false);
    }
  };

  const handleOpenProject = (projectId: string, path = "script/breakdown") => {
    const project = projects.find((p) => p.projectId === projectId);
    if (project?.archived || !effectiveWorkspaceId) {
      return;
    }
    navigate(getProjectPath(effectiveWorkspaceId, projectId, path));
  };

  const handleProjectSettingsClick = (projectId: string) => {
    if (effectiveWorkspaceId) {
      navigate(getProjectPath(effectiveWorkspaceId, projectId, "settings/general"));
    }
  };

  // Dropping a screenplay anywhere on the page starts a production. Stands down
  // while the create flow is open so its own dropzone gets the file.
  const screenplayDropRef = useRef(handleScreenplayFile);
  screenplayDropRef.current = handleScreenplayFile;
  const pageDropEnabled = canCreateProject && !isCreateProjectOpen && !isLoading;
  useEffect(() => {
    if (!pageDropEnabled) {
      setIsDraggingFile(false);
      return;
    }
    let depth = 0;
    const onDragEnter = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      depth += 1;
      setIsDraggingFile(true);
    };
    const onDragLeave = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setIsDraggingFile(false);
    };
    const onDragOver = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    };
    const onDrop = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      depth = 0;
      setIsDraggingFile(false);
      const file = event.dataTransfer?.files?.[0];
      if (file) screenplayDropRef.current(file);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, [pageDropEnabled]);

  const {
    summaryByProjectId,
    loadFailed: summaryLoadFailed,
    refetch: refetchSummary,
  } = useProjectsSummary(effectiveWorkspaceId || undefined);

  // Catalog rows are slim; merge in the lean summary (features, members,
  // descriptions, version counts) so list views can show real state.
  const enrichedProjects = useMemo(
    () =>
      projects.map((project) => {
        const summary = summaryByProjectId.get(project.projectId);
        if (!summary) {
          return project;
        }
        return {
          ...project,
          description: project.description ?? summary.description ?? undefined,
          versionsCount: project.versionsCount ?? summary.versionsCount,
          teamSummary: summary.members,
          features: summary.features
            ? {
                ...project.features,
                hasScriptBreakdown: summary.features.hasScriptBreakdown,
                hasCharacterBreakdown: summary.features.hasCharacterBreakdown,
                hasSchedule: summary.features.hasSchedule,
                hasBudget: summary.features.hasBudget,
                hasCoverage: summary.features.hasCoverage,
              }
            : project.features,
        };
      }),
    [projects, summaryByProjectId]
  );

  const isSearching = Boolean(searchTerm.trim());

  const gridProjects = useMemo(() => {
    let filtered = enrichedProjects;

    if (isSearching) {
      const searchLower = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((project) => {
        const displayType = productionTypeMapping[project.productionType] ?? "";
        return (
          project.name.toLowerCase().includes(searchLower) ||
          project.description?.toLowerCase().includes(searchLower) ||
          project.productionType?.toLowerCase().includes(searchLower) ||
          displayType.toLowerCase().includes(searchLower)
        );
      });
    }

    const sorted = [...filtered];
    if (sortBy === "newest") {
      sorted.sort((a, b) => createdAtValue(b) - createdAtValue(a));
    } else if (sortBy === "oldest") {
      sorted.sort((a, b) => createdAtValue(a) - createdAtValue(b));
    } else {
      sorted.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    }

    return {
      active: sorted.filter((project) => !project.archived),
      archived: sorted.filter((project) => project.archived),
    };
  }, [enrichedProjects, isSearching, searchTerm, sortBy]);

  // First visit with no real projects: open the seeded demo once, so new users
  // see a finished breakdown instead of an empty page. Owners only, and only a
  // demo in this workspace, so invited members stay where they were invited.
  const isLandingReady =
    !isLoading && !isProjectsError && !isWorkspaceAuthLoading && Boolean(effectiveWorkspaceId);
  const landingParams = {
    projects,
    userId: profile?.user_id,
    canCreateProject: canCreateWorkspaceProject,
    workspaceId: effectiveWorkspaceId,
    isWorkspaceOwner: currentWorkspace?.role?.toLowerCase() === WorkspaceRole.OWNER,
  };
  const demoLandingCandidate = isLandingReady ? pickDemoLandingProject(landingParams) : null;
  // A social signup's demo is still being copied when this page first loads,
  // so keep checking for it for a short while.
  const { landingAllowed } = useSeededDemoWait({
    workspaceId: effectiveWorkspaceId,
    isReady: isLandingReady,
    hasLandingCandidate: Boolean(demoLandingCandidate),
    shouldWait:
      isLandingReady &&
      shouldWaitForSeededDemo({ ...landingParams, accountCreatedAt: session?.created_at }),
    refetch: refetchCatalog,
  });
  const demoLandingProject = landingAllowed ? demoLandingCandidate : null;

  useEffect(() => {
    if (!demoLandingProject || !profile?.user_id) return;
    markDemoLandingSeen(profile.user_id);
    trackEvent(ANALYTICS_EVENTS.ONBOARDING_DEMO_LANDING_SHOWN, {
      project_id: demoLandingProject.projectId,
      workspace_id: effectiveWorkspaceId,
    });
    navigate(
      getProjectPath(effectiveWorkspaceId, demoLandingProject.projectId, "script/breakdown"),
      {
        replace: true,
      }
    );
  }, [demoLandingProject, profile?.user_id, effectiveWorkspaceId, trackEvent, navigate]);

  // A removed member who signs back in lands on the old workspace's link, which
  // has no projects for them. Recheck a list loaded before this page opened,
  // then move them to the workspace "/" would open.
  const isWorkspaceGone =
    Boolean(catalog && workspaceId) &&
    !catalog?.workspaces.some(
      (workspace) => String(workspace.workspace_id) === String(workspaceId)
    );
  const replacementWorkspace = isWorkspaceGone
    ? pickLandingWorkspace(
        catalog?.workspaces ?? [],
        preferencesContext?.preferences.navigation?.lastWorkspaceId
      )
    : null;

  useEffect(() => {
    if (!replacementWorkspace || isCatalogFetching) return;
    if (!isCatalogFetchedAfterMount) {
      void refetchCatalog();
      return;
    }
    navigate(
      PATH_WORKSPACE.workspaceProjects.replace(":workspaceId", replacementWorkspace.workspace_id),
      { replace: true }
    );
  }, [
    replacementWorkspace,
    isCatalogFetching,
    isCatalogFetchedAfterMount,
    refetchCatalog,
    navigate,
  ]);

  if (isLoading || demoLandingProject || replacementWorkspace) {
    return <ProjectsLoadingState />;
  }

  if (isProjectsError) {
    return (
      <div className="h-full w-full min-h-0 overflow-y-auto overflow-x-hidden bg-background">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 p-4 pb-12 lg:p-8">
          <ProjectsHeader subtitle={workspaceName} />
          <ProjectsLoadErrorState onRetry={() => void refetchCatalog()} />
        </div>
      </div>
    );
  }

  const activeProductionCount = productionProjects.filter((project) => !project.archived).length;
  const subtitleCount =
    activeProductionCount === 0
      ? "No productions yet"
      : `${activeProductionCount} ${activeProductionCount === 1 ? "production" : "productions"}`;
  const subtitle = workspaceName ? `${workspaceName} · ${subtitleCount}` : subtitleCount;

  const hasDemo = projects.some((project) => project.isDemo && !project.archived);
  // The AI apps promo hides itself once any AI app is connected.
  const aiAppsPromo =
    showAiAppsPromo && effectiveWorkspaceId ? (
      ENVS.mcpOAuthUrl ? (
        <AiAppsPromo
          workspaceId={effectiveWorkspaceId}
          url={ENVS.mcpOAuthUrl}
          onDismiss={handleDismissAiAppsPromo}
        />
      ) : (
        <ClaudeConnectorCard
          workspaceId={effectiveWorkspaceId}
          variant="promo"
          onDismiss={handleDismissAiAppsPromo}
        />
      )
    ) : null;

  return (
    <div className="h-full w-full min-h-0 overflow-y-auto overflow-x-hidden bg-background">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 p-4 pb-12 lg:p-8">
        <ProjectsHeader subtitle={subtitle} />

        {aiAppsPromo}

        <section className="flex flex-col gap-4" aria-label="Productions">
          <ProjectsSearchAndFilters
            title={isFirstRun ? "In this workspace" : "All productions"}
            count={gridProjects.active.length}
            showSearch={enrichedProjects.length >= SEARCH_THRESHOLD || isSearching}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />
          {/* The cards still work from the catalog; only team and draft details are missing. */}
          {summaryLoadFailed && enrichedProjects.length > 0 ? (
            <div
              role="alert"
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-2.5"
            >
              <p className="text-sm text-muted-foreground">Couldn't load team and draft details</p>
              <Button
                type="button"
                variant="badge"
                color="gray"
                size="xs"
                onClick={() => void refetchSummary()}
              >
                Try again
              </Button>
            </div>
          ) : null}
          {isSearching && gridProjects.active.length + gridProjects.archived.length === 0 ? (
            <ProjectsEmptyState searchTerm={searchTerm} onClearSearch={() => setSearchTerm("")} />
          ) : (
            <ProjectsGrid
              projects={gridProjects.active}
              archivedProjects={gridProjects.archived}
              onOpenProject={handleOpenProject}
              onProjectSettingsClick={handleProjectSettingsClick}
              expandArchived={isSearching}
              leadingTile={
                isSearching ? null : (
                  <StartProduction
                    focal={isFirstRun}
                    isDragging={isDraggingFile}
                    canCreateProject={canCreateProject}
                    onFile={handleScreenplayFile}
                    onStartBlank={handleStartBlank}
                    onExploreDemo={
                      canCreateProject && isFirstRun && !hasDemo ? handleExploreDemo : undefined
                    }
                    hasDemo={hasDemo}
                    isDemoLoading={isDemoLoading}
                  />
                )
              }
            />
          )}
        </section>
      </div>

      {isDraggingFile && (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-background/70 p-6 backdrop-blur-sm"
        >
          <div className="flex w-full max-w-lg flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-primary bg-card/90 px-8 py-12 text-center shadow-2xl">
            <UploadCloud className="h-8 w-8 text-primary" />
            <p className="font-squada text-4xl uppercase leading-none text-foreground">
              Drop to start a production
            </p>
            <p className="text-sm text-muted-foreground">PDF, Final Draft, Fountain, or Celtx</p>
          </div>
        </div>
      )}

      {createProjectFlowElement}
    </div>
  );
};

export default ProjectsPage;
